import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from "react";
import { useAdvancedSettings } from "../lib/settingsAdvanced";
import { followBaseline, unsavedNote } from "../lib/unsaved";
import { CheckIcon } from "./icons";
import { ErrorNote, Spinner } from "./ui";

/**
 * The settings sheet's blocks (the "4c One sheet" design): every page is a
 * title block, then sections, stacked inside the one frosted sheet
 * (settings/route.tsx) and separated by hairlines — never cards. Blocks
 * share the --rago-sheet-x gutter (styles/app.css .rago-sheet-*); `flush`
 * sections hand the gutter to their rows so dividers run edge to edge. A
 * page that edits a draft saves it from the floating pill (UnsavedBar); the
 * footer (SheetFooter) is what the pages not moved to it yet still end in.
 */

export function SheetHeader(props: {
  title: string;
  lede?: ReactNode;
  action?: ReactNode;
  /** A state chip on the title's own row, at its right (Trakt's "Connected").
   * Unlike `action` it leaves the lede the full width, so on a phone the
   * lede is not squeezed into a column beside it. */
  status?: ReactNode;
  /** Something before the title block — a person's avatar on their page. */
  leading?: ReactNode;
}) {
  return (
    <header
      className={`rago-sheet-head flex flex-wrap justify-between gap-y-3 ${
        props.leading ? "items-center gap-x-[18px]" : "items-start gap-x-6"
      }`}
    >
      {props.leading ? <div className="shrink-0">{props.leading}</div> : null}
      <div className="min-w-0 flex-1">
        {props.status ? (
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <h1 className="rago-sheet-title">{props.title}</h1>
            {props.status}
          </div>
        ) : (
          <h1 className="rago-sheet-title">{props.title}</h1>
        )}
        {props.lede ? (
          <p className="mt-2.5 max-w-prose text-sm leading-relaxed text-[var(--rago-text-2)]">
            {props.lede}
          </p>
        ) : null}
      </div>
      {props.action ? (
        <div className="flex shrink-0 items-center gap-2 pt-2">{props.action}</div>
      ) : null}
    </header>
  );
}

export type StateChipTone = "ok" | "accent" | "warn" | "error" | "muted";

/**
 * Where a connection stands, in two words: SheetHeader's `status` (Trakt,
 * Open Subtitles). `live` announces a change of the words — for a page
 * whose own button changes them.
 */
export function StateChip(props: { tone: StateChipTone; label: string; live?: boolean }) {
  const cls = {
    ok: "border-[rgba(62,207,142,0.35)] bg-[rgba(62,207,142,0.08)] text-[var(--rago-ok)]",
    accent:
      "border-[var(--rago-accent-line)] bg-[var(--rago-accent-soft)] text-[var(--rago-accent-strong)]",
    warn: "border-[rgba(245,220,74,0.45)] bg-[rgba(245,220,74,0.08)] text-[var(--rago-warn)]",
    error: "border-[rgba(255,92,92,0.4)] bg-[rgba(255,92,92,0.08)] text-[var(--rago-error)]",
    muted: "border-[var(--rago-line-2)] bg-white/[0.04] text-[var(--rago-text-2)]",
  }[props.tone];
  return (
    <span
      aria-live={props.live ? "polite" : undefined}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] font-medium whitespace-nowrap select-none ${cls}`}
    >
      <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
      {props.label}
    </span>
  );
}

export function SheetSection(props: {
  title?: string;
  action?: ReactNode;
  /** "accent" paints the eyebrow amber — the one section that must not
   * read as routine (General's Danger zone). */
  tone?: "accent";
  /** Rows carry the gutter themselves (SheetRow) so dividers span the sheet. */
  flush?: boolean;
  children: ReactNode;
}) {
  const id = useId();
  const hasHead = Boolean(props.title || props.action);
  return (
    <section className="rago-sheet-section" aria-labelledby={props.title ? id : undefined}>
      {hasHead ? (
        <div className="rago-sheet-section-head flex items-center justify-between gap-3">
          {props.title ? (
            <h2 id={id} className={props.tone === "accent" ? "rago-caps rago-caps-accent" : "rago-caps"}>
              {props.title}
            </h2>
          ) : (
            <span />
          )}
          {props.action}
        </div>
      ) : null}
      {props.flush ? props.children : <div className="rago-sheet-section-body">{props.children}</div>}
    </section>
  );
}

/**
 * The inset card of the pages that group things (Sharing; Trakt and Open
 * Subtitles write the same recipe inline): the sheet's soft fill on the
 * 20px radius, clipped so a row's hover keeps the corners. `raised`: the
 * one that asks for an answer (a request to connect).
 */
export function SheetCard(props: { raised?: boolean; className?: string; children: ReactNode }) {
  return (
    <div
      className={`overflow-hidden rounded-[var(--rago-radius-l)] border ${
        props.raised
          ? "border-[var(--rago-line-2)] bg-white/[0.06]"
          : "border-[var(--rago-line-1)] bg-white/[0.03]"
      } ${props.className ?? ""}`}
    >
      {props.children}
    </div>
  );
}

/** Edge-to-edge row list for flush sections. */
export function SheetRows(props: { children: ReactNode }) {
  return <ul className="divide-y divide-[var(--rago-line-1)]">{props.children}</ul>;
}

export function SheetRow(props: {
  align?: "start" | "center";
  className?: string;
  children: ReactNode;
}) {
  return (
    <li
      className={`flex ${props.align === "start" ? "items-start" : "items-center"} justify-between gap-4 px-[var(--rago-sheet-x)] py-3.5 ${props.className ?? ""}`}
    >
      {props.children}
    </li>
  );
}

/** Empty/loading line inside a flush section (carries the gutter). */
export function SheetMessage(props: { loading?: boolean; children: ReactNode }) {
  return (
    <p
      className={`px-[var(--rago-sheet-x)] py-1 text-sm ${
        props.loading
          ? "flex items-center gap-2 text-[var(--rago-text-2)]"
          : "text-[var(--rago-text-3)]"
      }`}
    >
      {props.loading ? <Spinner /> : null}
      {props.children}
    </p>
  );
}

/** Gutter wrapper for a page-level note (an ErrorNote between blocks). */
export function SheetInset(props: { children: ReactNode }) {
  return <div className="px-[var(--rago-sheet-x)]">{props.children}</div>;
}

export function SheetLoading(props: { label?: string }) {
  return (
    <SheetSection>
      <p className="flex items-center gap-2 text-sm text-[var(--rago-text-2)]">
        <Spinner /> {props.label ?? "Loading…"}
      </p>
    </SheetSection>
  );
}

/** A section that is only a sentence ("update rago-server for this"). */
export function SheetNotice(props: { title?: string; children: ReactNode }) {
  return (
    <SheetSection title={props.title}>
      <p className="text-sm text-[var(--rago-text-3)]">{props.children}</p>
    </SheetSection>
  );
}

/**
 * The save status line: "Saving…" while a save runs, "Unsaved: …" while
 * dirty, "Saved." (or a page's own note) after a save. Live region so the
 * outcome is announced.
 */
export function SaveStatus(props: {
  saving?: boolean;
  unsaved?: readonly string[];
  saved?: boolean;
  savedNote?: ReactNode;
}) {
  const dirty = !!props.unsaved && props.unsaved.length > 0;
  const showSaved = !props.saving && !dirty && !!props.saved;
  const note = props.saving
    ? "Saving…"
    : dirty
      ? unsavedNote(props.unsaved!)
      : showSaved
        ? (props.savedNote ?? "Saved.")
        : null;
  return (
    <p
      role="status"
      aria-live="polite"
      className={`flex min-h-5 items-center gap-1.5 text-[13px] ${
        showSaved ? "text-[var(--rago-text-2)]" : "text-[var(--rago-text-3)]"
      }`}
    >
      {showSaved ? <CheckIcon size={14} className="shrink-0 text-[var(--rago-ok)]" /> : null}
      {note}
    </p>
  );
}

/**
 * The form's action row. `unsaved` undefined = no dirty tracking (the button
 * is live unless `disabled`); an empty list = clean, button disabled.
 */
export function SheetFooter(props: {
  label?: string;
  unsaved?: readonly string[];
  saved?: boolean;
  savedNote?: ReactNode;
  saving?: boolean;
  disabled?: boolean;
  error?: unknown;
  /** Set for a non-submit action; otherwise the button submits its form. */
  onClick?: () => void;
  /** Extra controls, right-aligned. */
  children?: ReactNode;
  /** Inside a section body instead of a hairline footer — Security's
   * "Change password" sits right under its checklist. */
  bare?: boolean;
}) {
  const clean = props.unsaved !== undefined && props.unsaved.length === 0;
  const body = (
    <>
      <ErrorNote error={props.error} />
      <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
        <button
          type={props.onClick ? "button" : "submit"}
          className="rago-btn rago-btn-fit"
          disabled={props.disabled || props.saving || clean}
          onClick={props.onClick}
        >
          {props.saving ? <Spinner /> : null}
          {props.label ?? "Save changes"}
        </button>
        <SaveStatus
          saving={props.saving}
          unsaved={props.unsaved}
          saved={props.saved}
          savedNote={props.savedNote}
        />
        {props.children ? (
          <div className="ml-auto flex items-center gap-2">{props.children}</div>
        ) : null}
      </div>
    </>
  );
  return props.bare ? (
    <div className="mt-3">{body}</div>
  ) : (
    <footer className="rago-sheet-foot">{body}</footer>
  );
}

/**
 * The floating "Unsaved changes" pill (Kenny's Claude Design, 2026-10; on
 * Settings › Trakt, Account, Quality, Player and a server's General,
 * Network, Library, Transcoder and Scheduled Tasks — Security's password
 * form keeps its footer, and Open Subtitles has no draft: signing in is
 * the save): it shows at the foot of the screen, centred in the sheet,
 * while something on the page differs from what is saved, with Revert and
 * Save in it. After a save it says "Saved"
 * for a moment and leaves; a save that failed keeps it, with the reason in
 * a note right above.
 *
 * The page's last block, and its root carries .rago-sheet-fill (styles/
 * app.css on the dock). The dock is always rendered: its sr-only status
 * line has to be there before it has something to announce.
 */
export function UnsavedBar(props: {
  /** Something on the page differs from what is saved. */
  dirty: boolean;
  saving?: boolean;
  /** The last save went through, and nothing was changed since. */
  saved?: boolean;
  /** What a save says in place of "Saved" (Quality and Player: one that
   * reached this device only). */
  savedLabel?: string;
  /** Why the last save failed, in the page's words. */
  error?: string | null;
  onSave: () => void;
  onRevert: () => void;
  /** Revert and Save leave with the pill: when the focus was on one of
   * them, the page says where it goes (the control changed last). */
  onFocusLost?: () => void;
}) {
  const editing = props.dirty || !!props.saving;
  const failed = editing && !props.saving && !!props.error;
  const pillRef = useRef<HTMLDivElement>(null);
  const hadFocus = useRef(false);
  const { onFocusLost } = props;
  useEffect(() => {
    if (editing || !hadFocus.current) return;
    hadFocus.current = false;
    // Only when the focus went with the button: it is on the body then.
    if (!document.activeElement || document.activeElement === document.body) onFocusLost?.();
  }, [editing, onFocusLost]);
  const press = (act: () => void) => () => {
    if (props.saving) return;
    hadFocus.current = !!pillRef.current?.contains(document.activeElement);
    act();
  };

  return (
    <div className="rago-save-dock">
      <p role="status" className="sr-only">
        {props.saving
          ? "Saving…"
          : props.dirty
            ? "Unsaved changes"
            : props.saved
              ? (props.savedLabel ?? "Changes saved")
              : ""}
      </p>
      {failed ? (
        <p role="alert" className="rago-save-note">
          {props.error}
        </p>
      ) : null}
      {editing ? (
        <div
          key="edit"
          ref={pillRef}
          role="region"
          aria-label="Unsaved changes"
          className="rago-glass rago-save-pill"
        >
          {/* Said by the status line above and by the region's name. */}
          <p aria-hidden className="flex items-center gap-2.5 text-sm font-medium text-[var(--rago-text-1)]">
            <span
              className={`h-[7px] w-[7px] shrink-0 rounded-full ${
                failed ? "bg-[var(--rago-error)]" : "bg-[var(--rago-accent)]"
              }`}
            />
            {failed ? (
              "Not saved"
            ) : (
              <span>
                Unsaved<span className="max-[380px]:hidden"> changes</span>
              </span>
            )}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="h-[38px] cursor-pointer rounded-full px-4 text-sm font-semibold text-[var(--rago-text-2)] transition-colors hover:bg-white/[0.07] hover:text-[var(--rago-text-1)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--rago-ring)] aria-disabled:cursor-default aria-disabled:opacity-55 aria-disabled:hover:bg-transparent max-[430px]:px-3"
              aria-disabled={props.saving ? true : undefined}
              onClick={press(props.onRevert)}
            >
              Revert
            </button>
            <button
              type="button"
              className="rago-btn"
              aria-disabled={props.saving ? true : undefined}
              onClick={press(props.onSave)}
            >
              {props.saving ? <Spinner size={14} /> : null}
              Save
            </button>
          </div>
        </div>
      ) : props.saved ? (
        <div key="saved" aria-hidden data-state="saved" className="rago-glass rago-save-pill">
          <p className="flex items-center gap-2 text-sm font-medium text-[var(--rago-text-1)]">
            <CheckIcon size={16} className="shrink-0 text-[var(--rago-ok)]" />
            {props.savedLabel ?? "Saved"}
          </p>
        </div>
      ) : null}
    </div>
  );
}

/**
 * For a page on the pill: the control changed last, which gets the focus
 * back when the pill leaves (UnsavedBar's onFocusLost). `onChange` goes on
 * the element around the page's controls — every control's change bubbles
 * to it. `fallback` is where the focus goes when the revert took that
 * control away or disabled it (a field that is only there, or only live,
 * while its checkbox says so): it is handed the control that was lost.
 */
export function useLastTouched(fallback?: (lost: HTMLElement) => HTMLElement | null) {
  const last = useRef<HTMLElement | null>(null);
  return {
    onChange: (e: FormEvent<HTMLElement>) => {
      last.current = e.target instanceof HTMLElement ? e.target : null;
    },
    focus: () => {
      const el = last.current;
      if (!el) return;
      const lost = !el.isConnected || el.matches(":disabled");
      (lost ? fallback?.(el) : el)?.focus({ preventScroll: true });
    },
  };
}

/**
 * For a page on the pill: the draft over a baseline (the saved values, from
 * the page's query), as `[draft, setDraft]`. The page shows `draft ??
 * baseline` and edits go through setDraft; when the baseline moves — a
 * refetch, a save's response — a draft that was never edited follows it and
 * an edited one keeps its edits (lib/unsaved followBaseline). `same` is the
 * page's module-level form comparison: an inline lambda would re-run the
 * effect every render.
 *
 * The previous baseline is read BEFORE setDraft is called. React computes a
 * state updater at once only while the fiber is idle; otherwise it runs
 * during the next render, after this effect has already moved the ref on.
 * The pages used to read the ref inside the updater, and so compared the
 * draft with the baseline that had just arrived instead of the one it was
 * seeded from: on a server switch the old server's draft stayed, and the
 * pill lit with nothing typed (Kenny, 2026-10-09). The settings layout now
 * also remounts a server page per server (settings/route.tsx), so a switch
 * never reaches this path; it remains the rule for a baseline that changes
 * in place.
 */
export function useDraft<T>(baseline: T | null, same: (a: T, b: T) => boolean) {
  const [draft, setDraft] = useState<T | null>(null);
  const prev = useRef<T | null>(null);
  useEffect(() => {
    const next = baseline;
    if (!next) return;
    const before = prev.current;
    prev.current = next;
    setDraft((d) => followBaseline(d, before, next, same));
  }, [baseline, same]);
  return [draft, setDraft] as const;
}

/**
 * For a text field on a page on the pill: Enter saves the page, as it would
 * in a form (there is none — the pill is the page's, not a form's). Not the
 * Enter that ends an IME composition.
 */
export function saveOnEnter(save: () => void) {
  return (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
    e.preventDefault();
    save();
  };
}

/** The page-wide Show/Hide advanced toggle (lib/settingsAdvanced), for a header action slot. */
export function AdvancedToggle() {
  const [advanced, setAdvanced] = useAdvancedSettings();
  return (
    <button
      type="button"
      className="rago-btn rago-btn-quiet rago-btn-sm"
      aria-pressed={advanced}
      onClick={() => setAdvanced(!advanced)}
    >
      {advanced ? "Hide advanced" : "Show advanced"}
    </button>
  );
}

/** A section body that is one headline, a muted line, and a right-hand
 * control — which wraps under the text where the row is too narrow for both
 * (General's Libraries and Danger zone, Account's Delete account). */
export function LeadRow(props: { title: ReactNode; hint: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
      <div className="min-w-0">
        <p className="text-[15px] font-medium text-[var(--rago-text-1)]">{props.title}</p>
        <p className="mt-1 text-sm text-[var(--rago-text-3)]">{props.hint}</p>
      </div>
      {props.action ? <div className="shrink-0">{props.action}</div> : null}
    </div>
  );
}

/**
 * A read-only value in the input skin, sitting among editable fields
 * (username, email + its Verified pill). A div, not an <input>: the badge
 * sits inline right after the text, and the text stays selectable.
 *
 * With an `action` (a WellAction: Account's Edit) the value wraps instead
 * of truncating — on a phone the button takes a quarter of the well, and an
 * address cut short is one nobody can check — and the badge drops under it
 * when the line cannot hold both.
 */
export function ReadOnlyValue(props: {
  id: string;
  label: string;
  value: string;
  badge?: ReactNode;
  mono?: boolean;
  action?: ReactNode;
  /** Ids of the lines that describe the value (the status under the well). */
  describedBy?: string;
}) {
  const well = (
    <div
      id={props.id}
      role="textbox"
      aria-readonly="true"
      aria-label={props.label}
      aria-describedby={props.describedBy}
      className={`rago-input rago-input-static flex items-center ${
        props.action ? "rago-input-action flex-wrap gap-x-2.5 gap-y-1" : "gap-2.5"
      }`}
    >
      <span
        className={`min-w-0 ${props.action ? "[overflow-wrap:anywhere]" : "truncate"} ${
          props.mono ? "font-mono text-[13px]" : ""
        }`}
      >
        {props.value}
      </span>
      {props.badge}
    </div>
  );
  return props.action ? <WellWithAction action={props.action}>{well}</WellWithAction> : well;
}

/**
 * A well (a ReadOnlyValue, or an input carrying .rago-input-action) with a
 * WellAction inside it, at the right. The action is the well's SIBLING — a
 * button inside role="textbox" would be read as part of the value — and
 * sits in an absolutely positioned span: the span is what is centred, so
 * the button's own hover lift (.rago-btn:hover's transform) never fights
 * the centring.
 */
export function WellWithAction(props: { action: ReactNode; children: ReactNode }) {
  return (
    <div className="relative">
      {props.children}
      <span className="absolute top-1/2 right-[7px] flex -translate-y-1/2">{props.action}</span>
    </div>
  );
}

/**
 * The small button inside a well. Always type="button": it sits inside the
 * page's form and must never submit it. Under 430px it is its icon alone
 * (.rago-input-action gives the room back to the value); the label stays
 * for screen readers.
 */
export function WellAction(props: {
  icon: ReactNode;
  /** The visible label: "Edit". */
  children: string;
  /** The accessible name where the label alone does not say what it acts
   * on: "Edit username". */
  label?: string;
  hasPopup?: "dialog";
  /** Keep the focus where it is on a mouse press (Cancel beside the input
   * being edited: leaving the input would judge the value being dropped). */
  keepFocus?: boolean;
  ref?: Ref<HTMLButtonElement>;
  onClick: (e: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      ref={props.ref}
      type="button"
      className="rago-btn rago-btn-quiet rago-btn-sm"
      aria-label={props.label}
      aria-haspopup={props.hasPopup}
      onMouseDown={props.keepFocus ? (e) => e.preventDefault() : undefined}
      onClick={props.onClick}
    >
      {props.icon}
      <span className="max-[430px]:sr-only">{props.children}</span>
    </button>
  );
}
